import {FEATURE_FLAGS, MODULE_TYPES} from '../../../constants';
import {hasModule} from './ModuleUtil';

const CACHE = new Set();

const SESSION_STORAGE_KEY = 'availableFeatureFlags';

const isSessionStorageAvailable = () => typeof window !== 'undefined' && window.sessionStorage;

export type Feature = {key: string};
export const setAvailableFeatureFlags = (availableFeatureFlags: Feature[]): void => {
	if (Array.isArray(availableFeatureFlags)) {
		if (isSessionStorageAvailable()) {
			sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(availableFeatureFlags));
		}

		CACHE.clear();
		availableFeatureFlags.forEach(featureFlag => {
			CACHE.add(featureFlag.key);
		});
	}
};

export const hasFeatureFlag = (featureFlag: string): boolean => {
	if (CACHE.size === 0) {
		const sessionFeatureFlags = isSessionStorageAvailable() ? sessionStorage.getItem(SESSION_STORAGE_KEY) : null;
		const availableFeatureFlags = sessionFeatureFlags ? JSON.parse(sessionFeatureFlags) : [];
		setAvailableFeatureFlags(availableFeatureFlags);
	}

	return CACHE.has(featureFlag);
};

// The gen AI kill switch. Inverted polarity on purpose: a company that HAS this flag has lost AI,
// so this is the one flag whose presence takes a feature away rather than granting it.
//
// It never distinguishes a single account from the whole platform. The backend puts the key in
// every viewer's availableFeatureFlags when the platform-wide row is set, so the front end has one
// question to ask and one answer to act on. Centralised so every entry point and the banner read
// the same key.
export const isGenAiDisabled = (): boolean => hasFeatureFlag(FEATURE_FLAGS.GEN_AI_DISABLED);

export const hasTimeLocking = (): boolean => {
	return hasModule(MODULE_TYPES.TIME_LOCKING);
};

// The client-contacts feature (IGNITE-490/491/493/494) is gated by BOTH the
// `extending_client_contacts` feature flag and the NEW_CLIENT_CONTACTS_FEATURE module.
// Centralised here so the mandatory ≥2-week flag cleanup (AGENTS.md) is a single-site edit.
export const isClientContactsFeatureEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.EXTENDING_CLIENT_CONTACTS) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);

// The quotes stopgap is gated by BOTH the `quotes_builder` feature flag and the OPPORTUNITIES
// module (reused — quotes belong to opportunity projects, there is no separate quotes module).
// Centralised here so the mandatory ≥2-week flag cleanup is a single-site edit.
export const isQuotesEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.QUOTES_BUILDER) && hasModule(MODULE_TYPES.OPPORTUNITIES);

// Opportunities are gated by BOTH the `opportunities` feature flag and the OPPORTUNITIES module.
// Centralised here so the mandatory >=2-week flag cleanup is a single-site edit - same shape as
// isQuotesEnabled above.
export const isOpportunitiesEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.OPPORTUNITIES) && hasModule(MODULE_TYPES.OPPORTUNITIES);

// Gates the Xero invoice-number field (T4-122): the invoice details popup field, the All
// Invoices column, and its CSV export. No module gate - unlike the client-contacts/quotes/
// opportunities flags above, this isn't tied to a separate module, just the flag itself.
// Centralised here so the mandatory >=2-week flag cleanup (AGENTS.md) is a single-site edit.
export const isXeroInvoicingImprovementsEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.XERO_INTEGRATION_INVOICING_IMPROVEMENTS);

// A corrupt availableFeatureFlags entry in sessionStorage makes hasFeatureFlag's JSON.parse throw.
// The T4-197 Default tax rate field reads the flag through this, so its own reads never throw and a
// broken flag cache means "off" for it. Other reads of this flag on the same path are unguarded, so
// this does not keep the export modal up on a corrupt cache - it only adds no new way to throw.
export const isXeroInvoicingImprovementsEnabledOrOff = (): boolean => {
	try {
		return isXeroInvoicingImprovementsEnabled();
	} catch {
		return false;
	}
};

// Gates the QuickBooks invoice export defaults track (T4-148 onward). Centralised here so the
// mandatory >=2-week flag cleanup (AGENTS.md) is a single-site edit.
export const isQboInvoicingImprovementsEnabled = (): boolean =>
	hasFeatureFlag(FEATURE_FLAGS.QBO_INTEGRATION_INVOICING_IMPROVEMENTS);

// The T4-188 query fields are requested through this, the same way as the T4-197 Xero field: a corrupt flag
// cache means "off" for the request, so the read never throws and a flag-off window asks for what it did before.
export const isQboInvoicingImprovementsEnabledOrOff = (): boolean => {
	try {
		return isQboInvoicingImprovementsEnabled();
	} catch {
		return false;
	}
};

// Gates the Invoice Number field itself - the invoice details popup field, the All Invoices column
// and its CSV export, and the project invoicing table - for either integration that writes it: Xero
// (T4-122) or QuickBooks (T4-145). The Xero-only export features keep using
// isXeroInvoicingImprovementsEnabled, so the QuickBooks flag never switches them on.
export const isInvoiceNumberEnabled = (): boolean =>
	isXeroInvoicingImprovementsEnabled() || isQboInvoicingImprovementsEnabled();

// Unit4 (T4-103) is gated on the UNIT4 module alone, per Niels: "these should be rolled into 1
// module". The `use_unit4` / `unit4_error_log` feature flags are gone -- UNIT4 is granted per
// company rather than by tier, so the module is the entitlement. This covers the Unit4 Error Log
// too: it is part of the Unit4 integration, not a separately licensed feature.
export const isUnit4Enabled = (): boolean => hasModule(MODULE_TYPES.UNIT4);

// Harvest (T4-103) moved off the `show_harvest_integration` feature flag onto the HARVEST module.
// HARVEST is granted per company and is deliberately not part of any tier, so the module is the
// whole entitlement -- see TierTest.noTierGrantsHarvestModule.
export const isHarvestEnabled = (): boolean => hasModule(MODULE_TYPES.HARVEST);

export const hasTimeApproval = (): boolean => {
	return hasModule(MODULE_TYPES.TIME_LOCKING);
};

export const usesSso = (company?: {oktaEnabled?: boolean; oneloginEnabled?: boolean; AADEnabled?: boolean}): boolean => {
	const {oktaEnabled, oneloginEnabled, AADEnabled} = company || {};
	return !!oktaEnabled || !!oneloginEnabled || !!AADEnabled;
};

export const usingTimeApproval = (companyUseTimeApproval: boolean): boolean => {
	return companyUseTimeApproval && hasTimeApproval();
};

export const usingInternalTimeApproval = (companyUseInternalTimeApproval: boolean): boolean => {
	return hasTimeApproval() && companyUseInternalTimeApproval;
};

export const usingTimeOffApproval = (companyUseTimeOffApproval: boolean): boolean => {
	return companyUseTimeOffApproval && hasTimeApproval();
};
